

import * as React from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FileUploader } from "./file-uploader"
// Define type directly in component to avoid import issues
import { useToast } from "@/hooks/use-toast"
interface DocumentUploadData {
  title: string
  description?: string
  category?: string
  subcategory?: string
  file?: File
  document_type: 'pdf' | 'txt' | 'csv' | 'doc'
}
import { LoadingSpinner } from "@/components/shared/loading-spinner"
import { ACCEPTED_FILE_TYPES, documentTypeOf } from "@/lib/document-type"

interface DocumentUploadFormProps {
  categories: {
    id: string
    name: string
    subcategories: {
        id: string
        name: string
    }[]
  }[]
  onUpload: (document: DocumentUploadData) => Promise<void>
  onCancel: () => void
  loading: boolean
}

export function DocumentUploadForm({ categories, onUpload, onCancel, loading }: DocumentUploadFormProps) {
  const { toast } = useToast()
  const [title, setTitle] = React.useState("")
  const [description, setDescription] = React.useState("")
  const [selectedCategory, setSelectedCategory] = React.useState("")
  const [selectedSubcategory, setSelectedSubcategory] = React.useState("")
  const [file, setFile] = React.useState<File | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title || !file) return

    const document_type = documentTypeOf(file.name)
    if (!document_type) {
      toast({
        title: "Tipo de archivo no soportado",
        description: `Tipos permitidos: ${ACCEPTED_FILE_TYPES}`,
        variant: "destructive",
      })
      return
    }

    const uploadData: DocumentUploadData = {
      title,
      file,
      description: description || undefined,
      category: selectedCategory || undefined,
      subcategory: selectedSubcategory || undefined,
      document_type: document_type,
    }

    await onUpload(uploadData)
  }

  const selectedCategoryObj = categories.find((cat) => cat.id === selectedCategory)

  return (
    <div className="relative w-full space-y-4">
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-background/80 backdrop-blur-sm">
            <div className="text-center">
              <LoadingSpinner size="lg" text="Processing document..." />
              {file && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Processing: {file.name}
                </p>
              )}
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm font-medium">Título del Documento</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ingresa el título del documento"
              required
              disabled={loading}
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-medium">Descripción</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descripción breve del contenido del documento"
              rows={3}
              disabled={loading}
              className="resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category" className="text-sm font-medium">Categoría</Label>
              <Select
                value={selectedCategory || "__none__"}
                onValueChange={(value) => {
                  if (value === "__none__") {
                    setSelectedCategory("")
                    setSelectedSubcategory("")
                    return
                  }
                  setSelectedCategory(value)
                  setSelectedSubcategory("")
                }}
                disabled={loading}
              >
                <SelectTrigger id="category" className="h-10">
                  <SelectValue placeholder="Selecciona una categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin categoría</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subcategory" className="text-sm font-medium">Subcategoría</Label>
              <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory} disabled={!selectedCategory || loading}>
                <SelectTrigger id="subcategory" className="h-10">
                  <SelectValue placeholder="Selecciona una subcategoría" />
                </SelectTrigger>
                <SelectContent>
                  {selectedCategoryObj?.subcategories.map((subcat) => (
                    <SelectItem key={subcat.id} value={subcat.id}>
                      {subcat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Archivo del Documento</Label>
            <FileUploader
              onFileSelect={setFile}
              acceptedFileTypes={ACCEPTED_FILE_TYPES}
              selectedFile={file}
            />
          </div>
        </form>

      </div>
      
      <div className="flex justify-end gap-2 border-t pt-4">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} disabled={!title || !file || loading}>
          {loading ? (
            <LoadingSpinner size="sm" text="Subiendo..." />
          ) : (
            "Subir Documento"
          )}
        </Button>
      </div>
    </div>
  )
}
